import React, { useEffect } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, useLocation } from "react-router-dom";
import { Toaster, toast } from "sonner";

import { AuthProvider } from "@/context/AuthContext";
import { SettingsProvider, useSettings } from "@/context/SettingsContext";
import { CartProvider } from "@/context/CartContext";
import { VisitTracker } from "@/components/VisitTracker";

import AuthCallback from "@/pages/AuthCallback";
import ErrorBoundary from "@/components/ErrorBoundary";
import Layout from "@/components/Layout";
import Home from "@/pages/Home";
import Menu from "@/pages/Menu";
import Offers from "@/pages/Offers";
import Orders from "@/pages/Orders";
import Account from "@/pages/Account";
import Login from "@/pages/Login";
import Register from "@/pages/Register";
import Checkout from "@/pages/Checkout";
import OrderTrack from "@/pages/OrderTrack";
import About from "@/pages/About";
import Contact from "@/pages/Contact";
import Legal from "@/pages/Legal";
import PaymentResult from "@/pages/PaymentResult";
import ForgotPassword from "@/pages/ForgotPassword";
import ResetPassword from "@/pages/ResetPassword";

import AdminLayout from "@/pages/admin/AdminLayout";
import AdminDashboard from "@/pages/admin/AdminDashboard";
import AdminOrders from "@/pages/admin/AdminOrders";
import AdminMenu from "@/pages/admin/AdminMenu";
import AdminOffers from "@/pages/admin/AdminOffers";
import AdminReviews from "@/pages/admin/AdminReviews";
import AdminAnalytics from "@/pages/admin/AdminAnalytics";
import AdminSettings from "@/pages/admin/AdminSettings";
import AdminCustomers from "@/pages/admin/AdminCustomers";
import AdminScanner from "@/pages/admin/AdminScanner";
import AdminHomeBuilder from "@/pages/admin/AdminHomeBuilder";
import AdminChat from "@/pages/admin/AdminChat";
import SuperAdmin from "@/pages/SuperAdmin";
import BranchRegister from "@/pages/BranchRegister";
import StaffOrders from "@/pages/StaffOrders";
import StaffDisplay from "@/pages/StaffDisplay";
import Display from "@/pages/Display";
import AdminStaff from "@/pages/admin/AdminStaff";
import AdminPayouts from "@/pages/admin/AdminPayouts";

// Pages in MANAGED_SEO_PATHS set their own per-page SEO via useSeo(); the global
// settings-driven defaults below apply to every other route.
const MANAGED_SEO_PATHS = ["/", "/menu", "/checkout", "/offers", "/about"];
function SEO() {
  const { settings } = useSettings();
  const { pathname } = useLocation();
  useEffect(() => {
    if (MANAGED_SEO_PATHS.includes(pathname)) return;
    if (settings?.seo?.meta_title) document.title = settings.seo.meta_title;
    let meta = document.querySelector('meta[name="description"]');
    if (!meta) { meta = document.createElement("meta"); meta.name = "description"; document.head.appendChild(meta); }
    if (settings?.seo?.meta_description) meta.content = settings.seo.meta_description;
    let robots = document.querySelector('meta[name="robots"]');
    if (!robots) { robots = document.createElement("meta"); robots.name = "robots"; document.head.appendChild(robots); }
    robots.content = "index, follow";
  }, [settings, pathname]);
  return null;
}

function App() {
  useEffect(() => {
    const ref = new URLSearchParams(window.location.search).get("ref");
    if (ref) { try { localStorage.setItem("metros_ref", ref); } catch (e) { /* storage unavailable */ } }
  }, []);
  useEffect(() => {
    const onError = (e) => {
      // Surface uncaught errors (event handlers / async) that React error boundaries can't catch,
      // so users get feedback instead of a silent/blank failure.
      // eslint-disable-next-line no-console
      console.error("Uncaught app error:", e.error || e.reason || e);
      toast.error("Something went wrong. Please try again.");
    };
    window.addEventListener("error", onError);
    window.addEventListener("unhandledrejection", onError);
    return () => {
      window.removeEventListener("error", onError);
      window.removeEventListener("unhandledrejection", onError);
    };
  }, []);
  return (
    <BrowserRouter>
      <AuthProvider>
        <CartProvider>
          <SettingsProvider>
            <SEO />
            <VisitTracker />
            <Toaster position="top-center" richColors />
            <ErrorBoundary>
              <AppRoutes />
            </ErrorBoundary>
          </SettingsProvider>
        </CartProvider>
      </AuthProvider>
    </BrowserRouter>
  );
}

function AppRoutes() {
  const location = useLocation();
  // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
  if (location.hash?.includes("session_id=")) return <AuthCallback />;
  return (
            <Routes>
              <Route element={<Layout />}>
                <Route path="/" element={<Home />} />
                <Route path="/menu" element={<Menu />} />
                <Route path="/offers" element={<Offers />} />
                <Route path="/orders" element={<Orders />} />
                <Route path="/account" element={<Account />} />
                <Route path="/login" element={<Login />} />
                <Route path="/register" element={<Register />} />
                <Route path="/forgot-password" element={<ForgotPassword />} />
                <Route path="/reset-password" element={<ResetPassword />} />
                <Route path="/checkout" element={<Checkout />} />
                <Route path="/order/:orderNumber" element={<OrderTrack />} />
                <Route path="/payment/success" element={<PaymentResult mode="success" />} />
                <Route path="/payment/cancel" element={<PaymentResult mode="cancel" />} />
                <Route path="/about" element={<About />} />
                <Route path="/contact" element={<Contact />} />
                <Route path="/legal/:page" element={<Legal />} />
                <Route path="/branch-register" element={<BranchRegister />} />
              </Route>
              <Route path="/admin" element={<AdminLayout />}>
                <Route index element={<AdminDashboard />} />
                <Route path="orders" element={<AdminOrders />} />
                <Route path="chat" element={<AdminChat />} />
                <Route path="menu" element={<AdminMenu />} />
                <Route path="offers" element={<AdminOffers />} />
                <Route path="reviews" element={<AdminReviews />} />
                <Route path="staff" element={<AdminStaff />} />
                <Route path="customers" element={<AdminCustomers />} />
                <Route path="scanner" element={<AdminScanner />} />
                <Route path="payouts" element={<AdminPayouts />} />
                <Route path="analytics" element={<AdminAnalytics />} />
                <Route path="settings" element={<AdminSettings />} />
                <Route path="homepage" element={<AdminHomeBuilder />} />
              </Route>
              <Route path="/super" element={<SuperAdmin />} />
              <Route path="/staff" element={<StaffOrders />} />
              <Route path="/staff/display" element={<StaffDisplay />} />
              <Route path="/display" element={<Display />} />
            </Routes>
  );
}

export default App;
